import { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { AlertModal } from './AlertModal';

interface RerunBatchModalProps {
  batchId: string;
  batchName: string;
  cleanContactCount: number;
  onClose: () => void;
  onRerun: (data: RerunData) => Promise<void>;
}

export interface RerunData {
  batchName: string;
  enableLandline: boolean;
  enableTcpa: boolean;
}

export function RerunBatchModal({
  batchId: _batchId,
  batchName,
  cleanContactCount,
  onClose,
  onRerun,
}: RerunBatchModalProps) {
  const [newBatchName, setNewBatchName] = useState('');
  const [enableLandline, setEnableLandline] = useState(false);
  const [enableTcpa, setEnableTcpa] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  const showAlert = (title: string, message: string) => {
    setAlertModal({ isOpen: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, title: '', message: '' });
  };

  useEffect(() => {
    const now = new Date();
    const dateTime = now.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', '');
    
    setNewBatchName(`Reprocessed-${batchName} (${dateTime})`);
  }, [batchName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newBatchName.trim()) {
      showAlert('Validation Error', 'Please enter a batch name');
      return;
    }

    setIsSubmitting(true);
    try {
      await onRerun({
        batchName: newBatchName,
        enableLandline,
        enableTcpa,
      });
      onClose();
    } catch (error) {
      console.error('Failed to rerun batch:', error);
      showAlert('Error', 'Failed to rerun batch. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Re-Run Batch</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>{cleanContactCount}</strong> clean contacts from batch <strong>{batchName}</strong> will be reprocessed.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Batch Name
            </label>
            <input
              type="text"
              value={newBatchName}
              onChange={(e) => setNewBatchName(e.target.value)}
              placeholder="Enter batch name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              This will be the name of the new batch
            </p>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Validation Services
            </label>
            
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableLandline}
                  onChange={(e) => setEnableLandline(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <div>
                  <span className="text-sm text-gray-900">Check for Landlines</span>
                  <p className="text-xs text-gray-500">Verify numbers are mobile (rarely changes)</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableTcpa}
                  onChange={(e) => setEnableTcpa(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <div>
                  <span className="text-sm text-gray-900">Check for TCPA Risk</span>
                  <p className="text-xs text-gray-500">Verify numbers are safe to contact (recommended)</p>
                </div>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={isSubmitting}
            >
              <RefreshCw className={`w-4 h-4 ${isSubmitting ? 'animate-spin' : ''}`} />
              {isSubmitting ? 'Starting...' : 'Start Re-Run'}
            </button>
          </div>
        </form>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />
    </div>
  );
}
